import React, { useState, useMemo } from 'react';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { useOutstandingCollections } from '../../../hooks/useOutstandingCollections';
import { extractSummary, fmtUSD } from '../../../utils/outstandingUtils';
import { KpiStrip } from './KpiStrip';
import { AgingOverviewTab } from './AgingOverviewTab';
import { TopClientsTab } from './TopClientsTab';

// ─── Skeletons ─────────────────────────────────────────────────────────────────
const SkeletonCard: React.FC<{ h?: string }> = ({ h = 'h-32' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-md ${h} animate-pulse`} />
);

const LoadingSkeleton: React.FC = () => (
  <div className="p-6 space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} h="h-28" />)}
    </div>
    <SkeletonCard h="h-64" />
    <SkeletonCard h="h-72" />
  </div>
);

// ─── Tab switcher ──────────────────────────────────────────────────────────────
type Tab = 'aging' | 'top-clients';

const TabBar: React.FC<{
  active: Tab;
  onChange: (t: Tab) => void;
  agingCount: number;
  clientCount: number;
}> = ({ active, onChange, agingCount, clientCount }) => (
  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
    {([
      ['aging', 'Aging Overview', agingCount] as [Tab, string, number],
      ['top-clients', 'Top Clients', clientCount] as [Tab, string, number],
    ]).map(([key, label, count]) => (
      <button
        key={key}
        onClick={() => onChange(key)}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-semibold transition-colors ${
          active === key
            ? 'bg-white text-slate-800 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
        <span className={`text-[11px] px-1.5 py-0.5 rounded font-bold ${
          active === key ? 'bg-blue-50 text-blue-700' : 'bg-slate-200 text-slate-500'
        }`}>
          {count}
        </span>
      </button>
    ))}
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────────
export const OutstandingCollectionsPage: React.FC = () => {
  const { agingBuckets, topClients, dso, loading, error } = useOutstandingCollections();
  const [activeTab, setActiveTab] = useState<Tab>('aging');

  const summary = useMemo(() => extractSummary(agingBuckets), [agingBuckets]);

  const subtitle = !loading && agingBuckets.length > 0
    ? `Total outstanding: ${fmtUSD(summary.grandTotal)} · ${summary.overduePct.toFixed(1)}% overdue`
    : undefined;

  if (error) {
    return (
      <AppLayout>
        <PageHeader title="Outstanding & Collections" />
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-1">Failed to load data</p>
            <p className="text-slate-600 text-sm">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader title="Outstanding & Collections" subtitle={subtitle} />

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="p-6 space-y-4">
          {/* Shared KPI strip */}
          <KpiStrip summary={summary} dso={dso} />

          {/* Tab switcher */}
          <TabBar
            active={activeTab}
            onChange={setActiveTab}
            agingCount={agingBuckets.length}
            clientCount={topClients.length}
          />

          {/* Tab content */}
          {activeTab === 'aging' ? (
            <AgingOverviewTab buckets={agingBuckets} />
          ) : (
            <TopClientsTab clients={topClients} />
          )}
        </div>
      )}
    </AppLayout>
  );
};

export default OutstandingCollectionsPage;
